import type { AgentArtefactType, AgentRowTable } from '@septima/contract';
import { AGENT_ROW_TABLES } from '@septima/contract';
// AgentTask artefact-table lookup — ADR-0002 / PRD #65.
//
// A frontend presentation concern, not an ontology mirror (ADR-0008): the web
// app and the wire contract are a presentation projection of the ontology, so
// the artefact-type → table map is re-expressed locally over the
// `AgentArtefactType` union in `@/data/types`, never imported from the
// ontology runtime.
//
// Maps every `AgentArtefactType` (the type key an agent stamps on its
// artefact) to its table name. The UI uses this to dereference a thin
// `AgentTask` envelope (carrying just `(artefactType, artefactId)`) to the
// concrete artefact row holding the rich content (summary line, draft body,
// finding evaluations, etc.).
//
// The map is exhaustive against the `AgentArtefactType` union. CI fails
// the typecheck if a new artefact key is added to the union without an
// entry here — `Record<AgentArtefactType, string | null>` enforces the
// closed set. Adding agent #18 means:
//
//   1. Add its artefact key to `AgentArtefactType` in `@/data/types`.
//   2. Add an entry below — the artefact table or `null` if the type isn't
//      yet backed by a per-artefact table. MASON breaks now flow through
//      the reconciliation-break read path (no per-artefact table); a few
//      legacy AEGIS finding types still use compliance_alerts and have no
//      dedicated artefact table.
//
// `artefactTableNameFor()` returns `null` for keys with no mapping; callers
// must handle that — typically by falling back to the legacy
// `triggerDetail` line on `AgentTask`. The helper deliberately rejects
// unknown strings (returning `null`) so an out-of-date enum doesn't
// silently bind to a typo'd table name.

const ARTEFACT_TABLES: Record<AgentArtefactType, string | null> = {
    // ATLAS — post-call + monthly narrative
    'call-extracted-fact': 'call_extracted_facts',
    'call-action-item': 'call_action_items',
    'call-kb-note': 'call_kb_notes',
    'monthly-narrative-draft': 'wealth_monthly_narrative_drafts',

    // VIGIL — suitability detector
    'vigil-finding': 'wealth_vigil_findings',

    // AEGIS — KYC / breach / Consumer Duty / sanctions. PRD-#64 emitted these
    // as ontology Object Types but the per-row artefact tables are deferred
    // to PRD-#67 (vendor wiring). Until then the legacy tables are the
    // durable record and the runner stamps the legacy id.
    'kyc-freshness-finding': null,
    'breach-draft-finding': null,
    'cd-rescore-finding': null,
    'sanctions-hit-finding': null,

    // MASON — reconciliation. The reconciliation-break read path holds the
    // row; no dedicated artefact table.
    'reconciliation-classification-finding': null,

    // CEDAR — CA notices extracted from an uploaded PDF.
    'coac-action-draft': 'coac_action_drafts',
    // CEDAR — on-demand plain-English CA breakdown.
    'coac-breakdown': 'coac_breakdowns',

    // SCRIVENER / HERALD / EMISSARY composers
    'suitability-letter-draft': 'suitability_letter_drafts',
    'onboarding-pack-draft': 'onboarding_pack_drafts',
    'client-communication-draft': 'client_communication_drafts',

    // WARDEN / SENTINEL / QUORUM / BEACON / PULSE detectors
    'onboarding-stall-finding': 'onboarding_stall_findings',
    'vulnerability-marker-finding': 'vulnerability_marker_findings',
    'pcf-deviation-finding': 'pcf_deviation_findings',
    'investment-trust-policy-finding': 'investment_trust_policy_findings',
    'data-freshness-finding': 'data_freshness_findings',

    // CHARTER / HAVEN / FORUM / ARBITER composers
    'fund-report-draft': 'fund_report_drafts',
    'vulnerability-adaptation-plan-draft': 'vulnerability_adaptation_plan_drafts',
    'governance-paper-draft': 'governance_paper_drafts',
    'fair-value-assessment-draft': 'fair_value_assessment_drafts',
};

/**
 * Map an artefact Object Type key to its SQLite table name.
 *
 * Returns `null` when the type is recognised in the union but no dedicated
 * artefact table backs it yet (legacy AEGIS / MASON outputs flow through
 * their pre-existing tables), or when the input is not a known artefact type.
 */
export function artefactTableNameFor(type: string | null | undefined): string | null {
    if (!type) return null;
    if (!(type in ARTEFACT_TABLES)) return null;
    return ARTEFACT_TABLES[type as AgentArtefactType];
}

// The allow-listed subset of artefact tables the generic `/api/agent/rows/:table`
// surface exposes (contract-owned `AGENT_ROW_TABLES`). Finding tables are
// deliberately excluded — that endpoint bypasses `ctx.read` ACLs, so findings
// must be dereferenced via the aggregator / trigger-detail path instead.
const AGENT_ROW_TABLE_SET: ReadonlySet<string> = new Set<string>(AGENT_ROW_TABLES);

function isAgentRowTable(table: string): table is AgentRowTable {
    return AGENT_ROW_TABLE_SET.has(table);
}

/**
 * Like {@link artefactTableNameFor}, but only resolves to tables the raw
 * `/api/agent/rows` surface actually serves (`AGENT_ROW_TABLES`). Any other
 * artefact type — every `*-finding` type in particular — returns `null`, so
 * finding tasks skip that fetch (which 400s for them) and fall back to their
 * `triggerDetail` line. Keeps drift a compile concern: the return type is the
 * contract-owned `AgentRowTable`, so a table dropped from the allow-list stops
 * type-checking at the call sites.
 */
export function agentRowTableFor(type: string | null | undefined): AgentRowTable | null {
    const table = artefactTableNameFor(type);
    if (table === null) return null;
    return isAgentRowTable(table) ? table : null;
}

/**
 * Exhaustive list of every artefact type key. Useful for tests that
 * walk the union — the type system enforces completeness in the map
 * declaration above.
 */
export const ARTEFACT_TYPES: ReadonlyArray<AgentArtefactType> = Object.keys(
    ARTEFACT_TABLES,
) as AgentArtefactType[];
